import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { EMPTY, from, throwError, type Observable } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

import { getApiUrl, getAuthConfig } from '../../config/runtime-config';

import type {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';

/**
 * Attaches an MSAL Bearer ACCESS token to requests aimed at this app's own API.
 *
 * It must be an access token for the API scope (`api://<client-id>/access_as_user`),
 * not the OIDC ID token. The API validates `aud` against the configured audience,
 * and because the SPA and the API share one app registration an ID token's `aud`
 * looks superficially correct — so it fails the access-token check instead, which
 * reads like a bad token rather than the wrong token.
 *
 * `acquireTokenSilent` uses the cached refresh token and performs no redirect
 * unless interaction is genuinely required.
 *
 * Ported from the Creator frontend's AuthTokenInterceptor.
 */
@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {
  private readonly msalService = inject(MsalService);

  /**
   * Matched against the runtime API URL rather than a build-time constant,
   * because the container injects the API URL into `window.__env` at startup.
   */
  private isOurApi(url: string): boolean {
    const apiUrl = getApiUrl();
    if (!apiUrl) {
      return false;
    }

    try {
      const target = new URL(url, window.location.origin);
      const api = new URL(apiUrl, window.location.origin);
      return target.origin === api.origin;
    } catch {
      return false;
    }
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const account = this.msalService.instance.getActiveAccount();

    // Only our own API gets a token, and only when someone is signed in.
    if (!account || !this.isOurApi(req.url)) {
      return next.handle(req);
    }

    return from(
      this.msalService.instance.acquireTokenSilent({
        scopes: getAuthConfig().apiScopes,
        account,
      }),
    ).pipe(
      mergeMap((result) =>
        next.handle(
          req.clone({ setHeaders: { Authorization: `Bearer ${result.accessToken}` } }),
        ),
      ),
      catchError((err: unknown) => this.handleError(err)),
    );
  }

  private handleError(err: unknown): Observable<never> {
    if (err instanceof InteractionRequiredAuthError) {
      // Consent or re-authentication needed — restart auth interactively.
      this.msalService.instance.acquireTokenRedirect({
        scopes: getAuthConfig().apiScopes,
      });
      return EMPTY; // navigating away; cancel the in-flight request
    }

    if (err instanceof HttpErrorResponse && err.status === 401) {
      // The API rejected the token. Re-running sign-in interactively is the
      // only recovery; clearing the account first avoids a silent-retry loop.
      this.msalService.instance.setActiveAccount(null);
      this.msalService.instance.loginRedirect({
        scopes: getAuthConfig().apiScopes,
      });
      return EMPTY;
    }

    return throwError(() => err);
  }
}
